import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import API from "@/services/API";
import { useMutationWrapper } from "../../wrappers";
import { useInternationalization, useNotification } from "@/lib/contexts";
import { roomDetailsKey, roomListKey, getRoomsByOwnerIdKey } from "../room-query-keys";

interface UseMergeRoomParams {
  sourceRoomId: string;
  ownerId: string;
}

export const useMergeRoom = ({ sourceRoomId, ownerId }: UseMergeRoomParams) => {
  const { t } = useInternationalization("pages.rooms.details.settings.merge-room");
  const { success, error } = useNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutationWrapper<
    { mergedInto: string },
    { targetRoomId: string }
  >({
    mutationKey: ["merge-room", sourceRoomId],
    mutationFn: ({ targetRoomId }: { targetRoomId: string }) =>
      API.Rooms.mergeRoom(sourceRoomId, { targetRoomId }),
    onSuccess: (data: { mergedInto: string }) => {
      success(t("notifications.success"));
      queryClient.invalidateQueries({ queryKey: roomDetailsKey(sourceRoomId) });
      if (data?.mergedInto)
        queryClient.invalidateQueries({ queryKey: roomDetailsKey(data.mergedInto) });
      queryClient.invalidateQueries({ queryKey: roomListKey() });
      if (ownerId) {
        queryClient.invalidateQueries({ queryKey: getRoomsByOwnerIdKey(ownerId) });
      }
      // Redirect to rooms list after merge (simpler & avoids undefined/random id issues)
      navigate("/rooms");
    },
    onError: (e: Error) => {
      error(`${t("notifications.error")} ${e.message}`);
    },
  });

  return { merge: mutate, isMerging: isPending, t };
};
